import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {CommonModule} from "@angular/common";
import {
	WorkstationConfiguration,
	WorkstationConfigurationService, WorkstationExportCsv
} from "../../../../services/back/workstation-configuration.service";
import {CurrentTenantService} from "../../../../services/front/current-tenant.service";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {MatDialog} from "@angular/material/dialog";
import {ActivatedRoute, Router} from "@angular/router";
import {RightSliderService} from "../../../../services/front/right-slider.service";
import {
	distinctUntilChanged,
	finalize,
	forkJoin,
	from,
	merge,
	Observable,
	of,
	Subscription,
	switchMap,
	tap
} from "rxjs";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {ApplicationStatus, HealthStatus, HostingType} from "../../../../services/model/new-application.model";
import {DateUtils} from "../../../../utils/date.utils";
import {DataCollectionStatus, ServerTableData} from "../../../../services/model/server.model";
import {DesignSystemModule} from "../../../design-system/design-system.module";
import {MatMenu, MatMenuTrigger} from "@angular/material/menu";
import {ExportService, ExportType} from "../../../../services/front/export.service";
import {SnackbarModule} from "../../../global/snackbar/snackbar.module";
import {TopbarService} from "../../../../services/front/topbar.service";
import {EquipmentMenu} from "../../infrastructure/add-equipment/add-equipment.component";
import {QueryRangeType} from "../../../../services/back/tenant-finance.service";
import {WorkstationDetailComponent, WorkstationDetailInput} from "../workstation-detail/workstation-detail.component";
import {filter, first, map} from "rxjs/operators";
import {
	ServerDetailComponent,
	ServerDetailInput
} from "../../infrastructure/server/server-detail/server-detail.component";
import {EXTENSION_URL} from "../../../../models/home/navigation.model";
import {DisplayAppStatusComponent} from "../../../design-system/display-app-status/display-app-status.component";
import {DropdownTriggerDirective} from "../../../design-system/dropdown/dropdown-trigger.directive";
import {CriticalityLevel} from "../../../../services/tenant.service";
import {Organization, OrganizationService, OrganizationTree} from "../../../../services/organization.service";
import {TenantAccount} from "../../../../services/model/account.model";
import {Category} from "../../../../services/model/application-category.model";
import {CatalogTag} from "../../../../services/model/catalog-tag.model";

@Component({
  selector: 'app-workstation-list',
	imports: [
		CommonModule,
		DesignSystemModule,
		FormsModule,
		MatMenu,
		TranslateModule,
		ReactiveFormsModule,
		MatMenuTrigger,
		SnackbarModule,
		DropdownTriggerDirective
	],
  templateUrl: './workstation-list.component.html',
  styleUrl: './workstation-list.component.scss'
})
export class WorkstationListComponent implements OnInit, OnDestroy {
	tenantId: string;
	isEditor: boolean = false;

	_loading: boolean;
	_loadingFilter: boolean;
	_exporting: boolean = false;
	_refreshing: boolean = false;

	workstationRows: WorkstationConfiguration[] = [];
	workstationFilteredRows: WorkstationConfiguration[] = [];

	organizationList: OrganizationTree[] = [];
	osList: string[] = ['Windows', 'Mac'];
	statusList: DataCollectionStatus[] = Object.values(DataCollectionStatus).filter(status => status !== DataCollectionStatus.ERROR);
	healthList: HealthStatus[] = Object.values(HealthStatus).filter(health => health !== HealthStatus.UNKNOWN);

	openSearch: boolean = false;
	openFilters: boolean = false;
	filterForm: FormGroup;
	list: {
		organization: OrganizationTree[],
		os: string[],
		health: HealthStatus[],
		status: DataCollectionStatus[],
	};
	activeFilters: WorkstationFilter[] = [];
	form: typeof Form = Form;
	column: typeof WorkstationSortColumn = WorkstationSortColumn;
	direction: typeof SortDirection = SortDirection;

	private currentTenantService = inject(CurrentTenantService);
	private workstationConfigurationService = inject(WorkstationConfigurationService);
	private translate = inject(TranslateService);
	private rightSliderService = inject(RightSliderService);
	private router = inject(Router);
	private activatedRoute = inject(ActivatedRoute);
	private topBarService = inject(TopbarService);
	private exportService = inject(ExportService);
	private organizationService = inject(OrganizationService);

	loadingSub: Subscription = new Subscription();
	subscription: Subscription = new Subscription();

	ngOnInit(): void {
		this.createForm();
		this.subscription.add(this.currentTenantService.getCurrentTenantChanges()
			.pipe(tap(tenant => {
				this.tenantId = tenant.configuration.id
				this.isEditor = tenant.configuration.role !== 'read_only';
			}))
			.subscribe(() => this.initialize()));
		this.subscription.add(this.activatedRoute.queryParams
			.pipe(filter(params => params?.workstationId), first())
			.subscribe(params => this.openWorkstationDrawer(params.workstationId)));
	}

	openWorkstationDrawer(workstationId: string): void {
		const data: WorkstationDetailInput = {workstationId};

		this.resetQueryParamsObservable()
			.pipe(
				switchMap(() => this.rightSliderService.openComponent(WorkstationDetailComponent, data)),
				switchMap(() => this.fetchAllWorkstations(false)),
				tap(() => this.setWorkstationListByFilter()),
				switchMap(() => this.resetQueryParamsObservable())
			).subscribe();
	}

	private createForm(): void {
		this.filterForm = new FormGroup({
			[Form.name]: new FormControl(''),
			[Form.organization]: new FormControl([]),
			[Form.searchOrganization]: new FormControl(''),
			[Form.os]: new FormControl([]),
			[Form.status]: new FormControl([]),
			[Form.health]: new FormControl([]),
			[Form.sort]: new FormControl({column: WorkstationSortColumn.NAME, direction: SortDirection.ASC}),
		});

		this.subscription.add(merge(
			this.filterForm.get(Form.name)!.valueChanges
				.pipe(distinctUntilChanged()),
			this.filterForm.get(Form.organization)!.valueChanges,
			this.filterForm.get(Form.os)!.valueChanges,
			this.filterForm.get(Form.status)!.valueChanges,
			this.filterForm.get(Form.health)!.valueChanges,
			this.filterForm.get(Form.sort)!.valueChanges)
			.pipe(
				tap(() => this.setActiveFilters()),
				tap(() => this.setWorkstationListByFilter()))
			.subscribe());

		this.subscription.add(this.filterForm.get(Form.searchOrganization)!.valueChanges
			.subscribe(search => this.searchOrganizationFilter(search)));
	}

	initialize(): void {
		this.workstationRows = [];
		this.workstationFilteredRows = [];
		this.clearFilters(false);
		this.fetchWorkstationTableData();
		this.fetchFilterPanelData();
	}

	private setActiveFilters(): void {
		this.activeFilters = [
			...(this.filterForm.get(Form.organization)!.value as Organization[])
				.map(c => ({form: Form.organization, id: c.organizationId, value: c.name})),
			...(this.filterForm.get(Form.os)!.value as string[])
				.map(c => ({form: Form.os, id: c, value: c})),
			...(this.filterForm.get(Form.status)!.value as DataCollectionStatus[])
				.map(c => ({form: Form.status, id: c, value: this.translate.instant('page.infrastructure.overview.' + c.toLowerCase())})),
			...(this.filterForm.get(Form.health)!.value as HealthStatus[])
				.map(c => ({form: Form.health, id: c, value: this.translate.instant('page.dashboard.typology.' + c.toLowerCase())}))
		];
		if (this.activeFilters.length <= 0) {
			this.openFilters = false;
		}
	}

	private searchOrganizationFilter(search?: string): void {
		if (!search) {
			this.list.organization = this.organizationList;
		} else {
			const lowercaseValue = search.toLowerCase();
			this.list.organization = this.organizationList
				.reduce((acc: OrganizationTree[], org) => {
					const matchingChildren = org.children.filter(team =>
						team.organization.name.toLowerCase().includes(lowercaseValue)
					);

					if (org.organization.name.toLowerCase().includes(lowercaseValue) || matchingChildren.length > 0) {
						acc.push({
							...org,
							children: org.organization.name.toLowerCase().includes(lowercaseValue)
								? org.children
								: matchingChildren
						});
					}
					return acc;
				}, [])
				.sort((a, b) => a.organization.name.localeCompare(b.organization.name));
		}
	}

	private fetchFilterPanelData(): void {
		this.subscription.add(this.switchLoadingFilter()
			.pipe(
				switchMap(() => this.organizationService.getOrganizationTreeByTenantId(this.tenantId)),
				tap((organization) => this.buildFilterPanel(organization)),
				finalize(() => this.switchLoadingFilter()))
			.subscribe());
	}

	private buildFilterPanel(organization: OrganizationTree): void {
		this.organizationList = organization.children
			.filter(c => c.children.length > 0)
			.sort((a, b) => a.organization.name.localeCompare(b.organization.name));
		this.organizationList.forEach(o => o.children.sort((a, b) => a.organization.name.localeCompare(b.organization.name)));
		this.list = {
			organization: this.organizationList,
			status: this.statusList,
			os: this.osList,
			health: this.healthList,
		}
	}

	setWorkstationListByFilter(): void {
		const form: WorkstationTableFilterForm = this.buildWorkstationTableFilterForm();
		this.workstationFilteredRows = this.workstationRows
			.filter(workstation => {
				return (!form.name || workstation.hostname.toLowerCase().includes(form.name.toLowerCase()) || workstation.osModel.toLowerCase().includes(form.name.toLowerCase()) || workstation.manufacturer?.toLowerCase().includes(form.name.toLowerCase()) || workstation.osVersion.toLowerCase().includes(form.name.toLowerCase()) || workstation.serialNumber?.toLowerCase().includes(form.name.toLowerCase()) || workstation.userRealName?.toLowerCase().includes(form.name.toLowerCase()))
					&& (form.organization?.length === 0 || form.organization?.some(o => workstation.structureId === o.organizationId || workstation.teamId === o.organizationId))
					&& (form.os?.length === 0 || form.os?.some(o => workstation.osModel.toLowerCase().includes(o.toLowerCase())))
					&& (form.status?.length === 0 || form.status?.some(o => workstation.collectStatus === o))
					&& (form.health?.length === 0 || form.health?.some(o => workstation.health === o))
			})
			.sort(this.sortData);
	}

	private fetchWorkstationTableData(): void {
		this.loadingSub = this.setLoading(true)
			.pipe(
				switchMap(() => this.workstationConfigurationService.getTenantWorkstationConfiguration(this.tenantId)),
				tap(workstations => {
					this.topBarService.onTitleChange(
						this.translate.instant('menu.workstations'),
						this.translate.instant('menu.subtitle.workstations'),
						workstations.length.toString()
					);
					this.setWorkstationRows(workstations)
				}),
				finalize(() => this.setLoading(false)))
			.subscribe();
	}

	fetchAllWorkstations(loading = true): Observable<{}> {
		this.loadingSub.unsubscribe();
		return this.setLoading(loading).pipe(
			switchMap(form => this.workstationConfigurationService.getTenantWorkstationConfiguration(this.tenantId)),
			tap(workstations => this.setWorkstationRows(workstations)),
			switchMap(() => this.setLoading(false)));
	}

	private setWorkstationRows(workstations: WorkstationConfiguration[]): void {
		this.workstationRows = workstations;
		this.workstationRows.sort(this.sortData);
		this.workstationFilteredRows = this.workstationRows;
	}

	private buildWorkstationTableFilterForm(): WorkstationTableFilterForm {
		return {
			name: this.filterForm.get(Form.name)!.value?.trim().length > 0
				? this.filterForm.get(Form.name)!.value : null,
			organization: this.filterForm.get(Form.organization)!.value,
			os: this.filterForm.get(Form.os)!.value,
			status: this.filterForm.get(Form.status)!.value,
			health: this.filterForm.get(Form.health)!.value,
			sortDirection: this.sortFormValue.direction,
			sortColumn: this.sortFormValue.column,
		}
	}

	sortBy(column: WorkstationSortColumn): void {
		const sort: WorkstationSort = {
			column: column,
			direction: this.sortFormValue.column === column && this.sortFormValue.direction === SortDirection.ASC
				? SortDirection.DESC
				: SortDirection.ASC
		}
		if (!this._loading) {
			this.filterForm.get(Form.sort)!.setValue(sort, {emitEvent: false});
			this.workstationFilteredRows = this.workstationFilteredRows.sort(this.sortData);
		} else {
			this.filterForm.get(Form.sort)!.setValue(sort);
		}
	}

	private sortData = (data1: WorkstationConfiguration, data2: WorkstationConfiguration): number => {
		const direction: number = this.sortFormValue.direction === SortDirection.ASC ? 1 : -1;
		switch (this.sortFormValue.column) {
			case WorkstationSortColumn.NAME:
				if (data1.collectStatus !== data2.collectStatus) {
					const statusOrder: (DataCollectionStatus | null | undefined)[] = [
						DataCollectionStatus.ACTIVE,
						DataCollectionStatus.OUTDATED,
						DataCollectionStatus.ERROR,
						null,
						undefined
					];
					return (statusOrder.indexOf(data1.collectStatus) - statusOrder.indexOf(data2.collectStatus)) * direction;
				} else {
					return data1.hostname.localeCompare(data2.hostname) * direction;
				}
			case WorkstationSortColumn.HEALTH:
				const order: (HealthStatus | null | undefined)[] = [HealthStatus.GOOD, HealthStatus.NEEDS_IMPROVEMENT, HealthStatus.POOR, HealthStatus.UNKNOWN, null, undefined];
				return (order.indexOf(data2.health) - order.indexOf(data1.health)) * direction;
			case WorkstationSortColumn.USER:
				if (data1.userRealName && data2.userRealName) {
					return data1.userRealName.localeCompare(data2.userRealName) * direction;
				} else {
					return data1.userRealName ? -1 * direction : direction;
				}
			case WorkstationSortColumn.IP_ADDRESS:
				if (data1.privateIp && data2.privateIp) {
					return data1.privateIp.localeCompare(data2.privateIp) * direction;
				} else {
					return data1.externalIp ? -1 * direction : direction;
				}
			case WorkstationSortColumn.LOAD:
				return data1.load !== data2.load
					? ((data1.load ?? -1) > (data2.load ?? -1) ? 1 : -1) * direction
					: (data1.hostname.localeCompare(data2.hostname));
			case WorkstationSortColumn.STORAGE:
				return data1.storageUsed !== data2.storageUsed
					? ((data1.storageUsed ?? -1) > (data2.storageUsed ?? -1) ? 1 : -1) * direction
					: (data1.hostname.localeCompare(data2.hostname));
			default:
				return 0;
		}
	}

	removeFilter(filter: WorkstationFilter) {
		switch (filter.form) {
			case Form.organization:
				const organizationList: Organization[] = (this.filterForm.get(Form.organization)!.value as Organization[])
					.filter(c => c.organizationId !== filter.id)
				this.filterForm.get(Form.organization)!.setValue(organizationList);
				break;
			case Form.status:
				const statusList: DataCollectionStatus[] = (this.filterForm.get(Form.status)!.value as DataCollectionStatus[])
					.filter(c => c !== filter.id)
				this.filterForm.get(Form.status)!.setValue(statusList);
				break;
			case Form.os:
				const osList: string[] = (this.filterForm.get(Form.os)!.value as 'windows | mac'[])
					.filter(c => c !== filter.id)
				this.filterForm.get(Form.os)!.setValue(osList);
				break;
			case Form.health:
				const healthList: HealthStatus[] = (this.filterForm.get(Form.health)!.value as HealthStatus[])
					.filter(c => c !== filter.id)
				this.filterForm.get(Form.health)!.setValue(healthList);
				break;
			default:
				break;
		}
	}

	getDataCollectionStatusTooltip(dataCollectionStatus: DataCollectionStatus, date: string | null): string {
		const index: number = Object.keys(DataCollectionStatus).indexOf(dataCollectionStatus.toString());

		if (date && index < 2) {
			const nowUtcSeconds = Math.floor(Date.now() / 1000);  // Current UTC time in seconds
			const dateUtcSeconds = Math.floor(new Date(date).getTime() / 1000); // Convert Zulu date to UTC seconds
			switch (index) {
				case 0:
					return `Last collect ${DateUtils.secondsOrDhms(nowUtcSeconds - dateUtcSeconds)} ago.`;
				default:
					return `Last collect outdated (>20 min)<br>(${DateUtils.secondsOrDhms(nowUtcSeconds - dateUtcSeconds)} ago)`;
			}
		} else {
			switch (index) {
				case 2:
					return 'No data yet.';
				case 3:
					return 'Error while retrieving data.<br>Kapsul was unable to collect data.';
				default:
					return 'No data yet.';
			}
		}
	}

	openWorkstationRow(workstationId: string): void {
		const data: WorkstationDetailInput = {workstationId};

		this.resetQueryParamsObservable()
			.pipe(
				switchMap(() => this.rightSliderService.openComponent(WorkstationDetailComponent, data)),
				switchMap(() => this.fetchAllWorkstations(false)),
				tap(() => this.setWorkstationListByFilter()),
				switchMap(() => this.resetQueryParamsObservable())
			).subscribe();
	}

	deployMyAgents(): void {
		this.router.navigate([EXTENSION_URL])
	}

	clearFilters(emitEvent: boolean = true): void {
		this.openSearch = false;
		this.filterForm.setValue({
			[Form.name]: '',
			[Form.organization]: [],
			[Form.os]: [],
			[Form.status]: [],
			[Form.health]: [],
			[Form.searchOrganization]: '',
			[Form.sort]: this.sortFormValue
		}, {emitEvent: emitEvent});
	}

	clearInput(): void {
		this.filterForm.get(Form.name)!.setValue('');
	}

	openSearchPanel(input: HTMLInputElement): void {
		this.openSearch = !this.openSearch;
		if (this.openSearch) {
			input.focus();
		} else {
			this.filterForm.get(Form.name)!.setValue('');
		}
	}

	refresh(): void {
		this._refreshing = true;
		this.fetchAllWorkstations(false)
			.pipe(
				tap(() => this.setWorkstationListByFilter()),
				finalize(() => this._refreshing = false)
			).subscribe();
	}

	resetQueryParamsObservable(): Observable<boolean> {
		return from(
			this.router.navigate([], {
				queryParams: {
					'selectedWorkstationTab': null,
					'workstationId': null
				},
				queryParamsHandling: 'merge'
			})
		);
	}

	exportCsv = () => this.export(ExportType.CSV);
	exportXlsx = () => this.export(ExportType.XLSX);

	private export(type: ExportType): void {
		this.switchExporting()
			.pipe(
				map(() => this.buildWorkstationExportData(this.workstationFilteredRows)),
				tap(data => this.exportService.export(type, data, 'workstations')),
				finalize(() => this.switchExporting()))
			.subscribe();
	}

	private buildWorkstationExportData(data: WorkstationConfiguration[]): WorkstationExportCsv[] {
		return data.map((workstation: WorkstationConfiguration) => ({
			hostname: workstation.hostname,
			userRealName: workstation.userRealName || '',
			health: this.translate.instant('page.dashboard.typology.' + workstation.health.toLowerCase()),
			load: workstation.load + ' %',
			lastCollectDate: new Date(workstation.lastCollectDate).toLocaleString(),
			model: workstation.osModel + ' ' + workstation.osVersion,
			cpuUsed: workstation.cpuUsed + ' %',
			ramUsed: workstation.ramUsed + ' %',
			batteryHealth: workstation.batteryHealth ? workstation.batteryHealth + ' %' : '',
			storageLoad: workstation.storageLoad ? workstation.storageLoad + ' %' : '',
		}));
	}

	private switchLoadingFilter(): Observable<{}> {
		this._loadingFilter = !this._loadingFilter;
		return of({});
	}

	private setLoading(loading: boolean): Observable<{}> {
		this._loading = loading;
		return of({});
	}

	private switchExporting(): Observable<{}> {
		this._exporting = !this._exporting;
		return of({});
	}

	isRightSliderOpened(): boolean {
		return this.rightSliderService.isOpened();
	}

	get searchValue(): string {
		return this.filterForm.get(Form.name)!.value;
	}

	get sortFormValue(): WorkstationSort {
		return this.filterForm.get(Form.sort)!.value;
	}

	ngOnDestroy() {
		this.subscription.unsubscribe();
		this.loadingSub.unsubscribe();
	}

  protected readonly EquipmentMenu = EquipmentMenu;
	protected readonly QueryRangeType = QueryRangeType;
	protected readonly DataCollectionStatus = DataCollectionStatus;
}

enum WorkstationSortColumn {
	NAME = 'name',
	HEALTH = 'health',
	USER = 'user',
	IP_ADDRESS = 'ipAddress',
	LOAD = 'load',
	STORAGE = 'storage',
}

enum SortDirection {
	ASC = 'asc',
	DESC = 'desc'
}

interface WorkstationSort {
	column: WorkstationSortColumn;
	direction: SortDirection;
}

interface WorkstationFilter {
	form: Form;
	id: string;
	value: string;
}

enum Form {
	name = 'name',
	organization = 'organization',
	searchOrganization = 'searchOrganization',
	health = 'health',
	os = 'os',
	status = 'status',
	sort = 'sort',
}

interface WorkstationTableFilterForm {
	name?: string | null;
	organization?: Organization[];
	os?: string[];
	status?: DataCollectionStatus[];
	health?: HealthStatus[];
	sortDirection?: string;
	sortColumn?: string;
}
